import { PrebookHotelSummaryModel, SelectedHotelModel } from '@/__generated';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import React from 'react';
import HotelJapaneseName from '../Hotels/HotelJapaneseName';
import HotelStar from '../Hotels/HotelStar';
import SingleOrPluralText from '../SingleOrPluralText';
import moment from 'moment';
import DefaultCancellationPolicy from '@/pages/HotelDetail/components/DefaultCancellationPolicy';
import { faPauseCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HOTEL_CONFIG, PrivacyPolicyURL, TermsOfUseURL, appDisplayDateFormat } from '@/config';
import PlanTaxAndFee from '@/pages/HotelDetail/components/PlanTaxAndFee';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { UncontrolledTooltip } from 'reactstrap';

type Props = {
  bookingSummary: PrebookHotelSummaryModel;
  onPayment?: () => void;
  showSubmitButtons?: boolean;
};

export default function HotelSummary({ bookingSummary, onPayment, showSubmitButtons }: Props) {
  const selectedHotel = bookingSummary.selectedHotel;
  const totalPrice = bookingSummary.selectedHotel?.totalPrice;
  const { formatCurrency } = useCurrencyFormat();
  const numberOfNight = React.useMemo(() => {
    return moment(bookingSummary?.checkoutDate).diff(moment(bookingSummary?.checkinDate), 'day');
  }, [bookingSummary]);

  const hotelImage = React.useMemo(() => {
    if (selectedHotel?.images?.length) {
      return selectedHotel?.images[0];
    }
    return HOTEL_CONFIG.defaultImage;
  }, [selectedHotel]);
  return (
    <div className="px-30 py-30 border-light rounded-4">
      <div className="row x-gap-10">
        <div className="col">
          <div className="text-20 fw-500 mb-20">Your booking summary</div>
        </div>
      </div>
      <div className="row x-gap-15 y-gap-20 mb-10">
        <div className="col-md-4 col-xs-12">
          <img className="w-100 rounded-4 object-cover" src={hotelImage} />
        </div>
        <div className="col">
          <div className="row">
            <div className="lh-17 fw-500 col">
              {selectedHotel?.hotelName}
              <HotelJapaneseName name={selectedHotel?.hotelNameJp} addBefore="-" />
            </div>
            <HotelStar star={selectedHotel?.starRating || 0} />
            <div className="text-14 lh-15 mt-5">{selectedHotel?.address}</div>
          </div>
        </div>
      </div>

      <div className="border-top-light mt-10 mb-20" />
      <div className="row y-gap-20 justify-between">
        <div className="col-auto">
          <div className="text-15">Check-in</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(bookingSummary.checkinDate)}</div>
          {selectedHotel?.checkinTime ? <div className="text-15 text-light-1">{selectedHotel?.checkinTime}</div> : null}
        </div>
        <div className="col-auto md:d-none">
          <div className="h-full w-1 bg-border" />
        </div>
        <div className="col-auto text-right md:text-left">
          <div className="text-15">Check-out</div>
          <div className="fw-500">{appDisplayDateFormat.default.dateOnly(bookingSummary.checkoutDate)}</div>
          {selectedHotel?.checkoutTime ? <div className="text-15 text-light-1">{selectedHotel?.checkoutTime}</div> : null}
        </div>
      </div>
      {/* End row */}

      <div className="border-top-light mt-10 mb-10" />
      <div>
        <div className="text-15">Total length of stay:</div>
        <div className="fw-500">
          <SingleOrPluralText count={numberOfNight} singleText="{} night" pluralText="{} nights" />
        </div>
      </div>

      <div className="border-top-light mt-10 mb-10" />
      <div className="text-15">Selected room(s):</div>
      {selectedHotel?.rooms?.map((room, index) => {
        return (
          <div className="row  justify-between items-center" key={`${index}-${room.planId}`}>
            <div className="col fw-500">{room.roomTypeName}</div>
            <div className="col-auto">
              <div className="text-15 fw-500">
                <SingleOrPluralText count={room.noOfRooms || 0} singleText="{} room" pluralText="{} rooms" />
              </div>
            </div>
            <div className="col-12">
              <div className="">
                <div className="text-14 fw-500 text-blue-1">
                  {[
                    `Check-in time: ${room.checkinTime || selectedHotel?.checkinTime}`,
                    `Check-out time: ${room.checkoutTime || selectedHotel?.checkoutTime}`,
                  ].join(', ')}
                </div>
              </div>
              {room.remarks ? (
                <>
                  {room.supplier !== 'NTA' ? (
                    <div className="text-14 text-red-1 ">Additional charges may apply upon check in</div>
                  ) : (
                    <div className="text-14 text-red-1  items-center">
                      <b> Infant remarks:</b>
                      <div>{room.remarks}</div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="text-green-2">
              {room.planTitle ? (
                <div className={`text-14 fw-500  d-flex items-center`}>
                  <i className="icon-check text-12 mr-10" />
                  {room.planTitle}
                  {room.planDesc ? (
                    <div className="d-flex">
                      <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-planDesc-${index}`} />
                      <UncontrolledTooltip target={`room-planDesc-${index}`}>{room.planDesc}</UncontrolledTooltip>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={`text-14 fw-500  d-flex items-center`}>
                <i className="icon-check text-12 mr-10" />
                <PlanTaxAndFee plan={room} />
                {room.paymentNotes ? (
                  <div className="d-flex">
                    <FontAwesomeIcon icon={faCircleInfo} className="text-blue-1 font-16 ml-5" id={`room-paymentNotes-${index}`} />
                    <UncontrolledTooltip target={`room-paymentNotes-${index}`}>{room.paymentNotes}</UncontrolledTooltip>
                  </div>
                ) : null}
              </div>
              <div>
                <DefaultCancellationPolicy checkinDate={bookingSummary.checkinDate} plan={room as any} />
              </div>

              <div className="border-bottom-light pb-10" />
            </div>
          </div>
        );
      })}
      <div className="text-15">Total Price</div>
      <div className="text-center">
        <span className="fw-500 text-22 text-blue-1">{formatCurrency(totalPrice)}</span>
      </div>

      {showSubmitButtons ? (
        <>
          <div className="mt-5  d-flex x-gap-10">
            {bookingSummary.onHoldAllowance ? (
              <div className="col-auto">
                <button id="on-hold" type="submit" className="h-60 px-24 button  bg-red-1 text-18 text-white   -dark-1">
                  <FontAwesomeIcon icon={faPauseCircle} className="mr-15" />
                  On-hold
                </button>
              </div>
            ) : null}

            <div className="col">
              <button id="payment" type="submit" className="button w-100 h-60 px-24 -dark-1 bg-blue-1 text-white text-18">
                Next
                <i className="icon-arrow-top-right ml-15" />
              </button>
            </div>
          </div>
          <div className="mt-10 fw-500 text-14 text-red-1">
            By proceeding with this booking, I agree with NTA{' '}
            <a href={TermsOfUseURL} target="_blank" className="text-blue-1 underline">
              Terms of Use
            </a>
            {` and `}
            <a href={PrivacyPolicyURL} target="_blank" className="text-blue-1 underline">
              Privacy Policy
            </a>
            .
          </div>
        </>
      ) : null}

      {onPayment ? (
        <button onClick={onPayment} className="button h-60 w-100 mt-10 px-24 -dark-1 bg-green-2 text-white text-22">
          Complete Booking
          <i className="icon-arrow-top-right ml-15" />
        </button>
      ) : null}
    </div>
  );
}
